import {Injectable}                           from '@angular/core';
import {StatisticStore}                       from '../../../../stores/statistic/statistic.store';
import {StatisticService}                     from '../../../../services/data/statistic.service';
import {GetRoundNumericService}               from '../../../../ap-utils/service/get-round-numeric.service';
import {PpCommonService}                      from '../../../../plant-protection/services/pp-common.service';
import {NFertilizationPlantProtectionService} from '../../../../services/data/n-fertilization-plant-protection.service';
import {
  ApPpPlanningWizardOverviewStatsItem,
  IPpOverviewStatValues,
  IPpPlanningWizardOverviewGridData,
  IPpPlanningWizardSelectionGridData,
  IPpProductListGridItem
}                                             from '../pp-planning-wizard.types';

import {OperationModesEnum}    from '../../../../stores/common/operation-modes.store';
import {
  GenericChartConfig,
  GenericSeriesConfig,
  GenericSeriesData,
  GenericYAxisConfig
}                              from '../../../../statistics/components/charts/ap-generic-chart/ap-generic-chart.types';
import {
  nameOf
}                              from '../../../../ap-core/utils/ap-name-of';
import {Create, ObjectFactory} from 'ts-tooling';
import {
  ApTranslationService
}                              from '../../../../ap-utils/service/ap-translation.service';
import {
  PpPlanningWizardOverviewStatisticService
}                              from './pp-planning-wizard-overview-statistic.service';
import {ApStatsType}           from '../../../../ap-interface';
import IPlantProtectionProductList = Data.TaskManagement.IPlantProtectionProductList;

@Injectable({providedIn: 'root'})
export class PpPlanningWizardStatisticService {
  constructor(private statisticStore: StatisticStore,
              private statisticService: StatisticService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private ppCommonService: PpCommonService,
              private nFertilizationPlantProtectionService: NFertilizationPlantProtectionService,
              private ppPlanningWizardOverviewStatisticService: PpPlanningWizardOverviewStatisticService) {
  }

  /**
   * Generates the statistic charts for the overview step
   * @private
   */
  public generateOverviewStats(items: IPpPlanningWizardOverviewGridData[],
                               selectionData: IPpPlanningWizardSelectionGridData[],
                               productListGridItems: IPpProductListGridItem[]): void {
    // collect all necessary data to show statistics
    const statData = this.calculateStatData(items, productListGridItems);
    // Generate upper statistic table
    this.generateStatOverviewTable(statData, selectionData, items);
    // Only in the case of PPTargetMapPerPlan => Showing additional chart(s)
    if (items?.FirstOrDefault()?.DetailsData?.OperationModeAppSetting?.OperationMode === OperationModesEnum.PPTargetMapPerPlan) {
      this.generateStatOverviewChart(statData);
    }
  }

  /**
   * Generates a general statistic table which gives an overview of applied n/product/water etc.
   * @param statData the collected statistic data
   * @param selectionData the data from a selection step of the wizard
   * @param items the overview grid items
   * @private
   */
  private generateStatOverviewTable(statData: IPpOverviewStatValues, selectionData: IPpPlanningWizardSelectionGridData[], items: IPpPlanningWizardOverviewGridData[]): void {
    const dataForTitles = selectionData.map(x => ({
      AreaHa: x.Area,
      Selected: items.some(z => z.Id === x.Id)
    }));
    const statTitle = this.statisticService.generateFieldsAreaTitle(dataForTitles);
    const statOverviewItems: ApPpPlanningWizardOverviewStatsItem[] = [
      {
        key: this.translationService.translate('AvgNUptakeShort'),
        value: statData.NUptakeAreaWeightedAvg ? `${this.roundNumericService.roundAsNumber(statData.NUptakeAreaWeightedAvg, 0)} ${this.translationService.translate('Unit_Rate')}` : '-'
      },
      {
        key: this.translationService.translate('Global__Avg_Water'),
        value: `${statData.WaterAvg} ${this.translationService.translate('Api_Enums__Litre')}`
      },
      {
        key: this.translationService.translate('Global__Min_Water'),
        value: `${statData.WaterMin} ${this.translationService.translate('Api_Enums__Litre')}`
      },
      {
        key: this.translationService.translate('Global__Max_Water'),
        value: `${statData.WaterMax} ${this.translationService.translate('Api_Enums__Litre')}`
      },
      {
        key: this.translationService.translate('Nutrients__Average_Amount_Goods'),
        value: statData.ProductAreaWeightedAvg?.map(p => this.ppCommonService.buildProductExpr(p, {useBaseUnit: true}))?.join('\n')
      },
      {
        key: this.translationService.translate('Global__Sum_Goods'),
        value: statData.ProductSum?.map(p => this.ppCommonService.buildProductExpr(p, {useBaseUnit: true}))?.join('\n')
      }
    ];

    // provide items for the statistic store
    this.ppPlanningWizardOverviewStatisticService.generateStats(statTitle, statOverviewItems, statTitle);
  }

  /**
   * Generates a water/n-uptake chart for PPTargetMapPerPlan
   * @param statData the collected statistic data
   * @private
   */
  private generateStatOverviewChart(statData: IPpOverviewStatValues): void {
    // configure the chart and its axes
    const chartConfigs: GenericChartConfig[] = [];
    const chartConfig = new GenericChartConfig();
    chartConfig.pannable = false;
    chartConfig.zoomable = false;
    chartConfig.transitions = false;
    chartConfig.xAxisConfig.min = statData.NUptakeMin;
    chartConfig.xAxisConfig.max = statData.NUptakeMax;
    chartConfig.xAxisConfig.title = this.translationService.translate('N__ChartTitleX');
    chartConfig.noteField = nameOf<GenericSeriesData>('note');
    chartConfig.tooltipDelegate = (dataItem, _) => {
      const seriesData = dataItem as GenericSeriesData;
      if (!seriesData) {
        return '';
      }
      return this.createTooltipText(seriesData);
    };

    const yAxisConfig = new GenericYAxisConfig();
    yAxisConfig.type = 'numeric';
    yAxisConfig.min = 0;
    chartConfig.yAxisConfigs.push(yAxisConfig);

    // generate series data points
    const seriesConfig = new GenericSeriesConfig();
    seriesConfig.type = 'scatterLine';
    seriesConfig.lineWidth = 3;
    seriesConfig.markerVisible = false;
    seriesConfig.style = 'normal';
    this.generateSeriesData(statData, seriesConfig);
    chartConfig.seriesConfigs.push(seriesConfig);
    chartConfigs.push(chartConfig);

    // add chart to statistic store
    this.statisticStore.addStatisticData<GenericChartConfig>(
      chartConfigs, ApStatsType.GenericChart, this.translationService.translate('Global__Water'), ''
    );
  }

  /**
   * Generates chart series data points
   * @param statData the current statistic basedata
   * @param seriesConfig the series config which holds data points
   * @private
   */
  private generateSeriesData(statData: IPpOverviewStatValues, seriesConfig: GenericSeriesConfig): void {
    let hasMaxBreak = false;
    let hasMinBreak = false;
    let previousWaterAmount = -1;
    for (let nUptake = statData.NUptakeMin; nUptake <= statData.NUptakeMax; nUptake = nUptake + statData.NUptakeStepSize) {
      const seriesData = new GenericSeriesData();
      seriesData.xValue = this.roundNumericService.roundAsNumber(nUptake, 0);
      seriesData.yValue = statData.Slope * nUptake + (statData.WaterAvg - statData.Slope * statData.NUptakeAreaWeightedAvg);
      if (seriesData.yValue < statData.WaterMin) {
        seriesData.yValue = statData.WaterMin;
      }
      if (seriesData.yValue > statData.WaterMax) {
        seriesData.yValue = statData.WaterMax;
        if (!hasMaxBreak) {
          seriesConfig.seriesData[seriesConfig.seriesData.length - 1].note = this.createTooltipText(
            seriesConfig.seriesData[seriesConfig.seriesData.length - 1]);
          hasMaxBreak = true;
        }
      }
      if (previousWaterAmount !== -1 && previousWaterAmount !== seriesData.yValue && !hasMinBreak) {
        seriesConfig.seriesData[seriesConfig.seriesData.length - 1].note = this.createTooltipText(
          seriesConfig.seriesData[seriesConfig.seriesData.length - 1]);
        hasMinBreak = true;
      }
      if (Math.round(statData.NUptakeAreaWeightedAvg) === nUptake) {
        seriesData.yValue = this.roundNumericService.roundAsNumber(statData.WaterAvg);
        seriesData.note = this.createTooltipText(seriesData, 'Nutrients__Average_Amount_Nutrient_kg_N_per_ha');
      }
      previousWaterAmount = seriesData.yValue;
      seriesConfig.seriesData.push(seriesData);
    }
  }

  /**
   * Collects and calculates all the data which is required to show the statistics
   * @param items the current overview grid items
   * @param productListGridItems the currently selected product grid items
   * @private
   */
  private calculateStatData(items: IPpPlanningWizardOverviewGridData[], productListGridItems: IPpProductListGridItem[]): IPpOverviewStatValues {
    const defaultItem = items?.FirstOrDefault();
    const productsAvg: IPlantProtectionProductList[] = [];
    const productsSum: IPlantProtectionProductList[] = [];

    // calculate area weighted water average...
    const itemsWaterAreaAndAverage = items
      .map(item => ({
        Area: Create(item.SelectionData?.Area, 0),
        Average: item.PlanBook.ApplicationRate,
        Factor: undefined
      }));
    const waterAreaWeighted = this.nFertilizationPlantProtectionService.calculateAreaWeightedAverage(itemsWaterAreaAndAverage);

    // calculate area weighted product average and product sum
    for (const productListGridItem of productListGridItems) {
      const productListItem = productListGridItem.ProductListItem;
      const itemsProductAreaAndAverage = items
        .map(item => {
          // find the matching 'SelectedProduct' which contains pre-calculated productRate (per ha) for this field
          const productItemWithRateForField = item.PlanBook
            .ProductList?.FirstOrDefault(p => p?.Product?.Id === productListItem?.Product?.Id);
          return ({
            Area: Create(item.SelectionData?.Area, 0),
            Average: productItemWithRateForField?.ProductRate ?? 0, // already calculated productRate per field (done in wizard overview)
            Factor: undefined
          });
        });

      // calculate product sum based on pre-calculated rate per ha
      const productWithSum = ObjectFactory.Copy(productListItem);
      productWithSum.ProductRate = itemsProductAreaAndAverage.reduce((sum, a) => sum + a.Area * a.Average, 0);
      productsSum.push(productWithSum);

      // calculate area weighted product average
      const productWithAreaWeightedAvg = ObjectFactory.Copy(productListItem);
      productWithAreaWeightedAvg.ProductRate = this.nFertilizationPlantProtectionService.calculateAreaWeightedAverage(itemsProductAreaAndAverage);
      productsAvg.push(productWithAreaWeightedAvg);
    }

    return {
      NUptakeMin: this.roundNumericService.roundAsNumber(items.map(x => x.SelectionData?.ScanData?.NUptakeMin)?.Min() ?? 0, 0),
      NUptakeMax: this.roundNumericService.roundAsNumber(items.map(x => x.SelectionData?.ScanData?.NUptakeMax)?.Max() ?? 0, 0),
      NUptakeStepSize: 1,
      NUptakeAreaWeightedAvg: defaultItem.DetailsData.FormData.NUptakeAverage,
      Slope: defaultItem.SlopeForJob * -1,
      WaterAvg: this.roundNumericService.roundAsNumber(waterAreaWeighted ?? 0, 0),
      WaterMin: this.roundNumericService.roundAsNumber(defaultItem.PlanBook?.Summary?.UserApplicationMinimum ?? 0, 0),
      WaterMax: this.roundNumericService.roundAsNumber(defaultItem.PlanBook?.Summary?.UserApplicationMaximum ?? 0, 0),
      ProductSum: productsSum,
      ProductAreaWeightedAvg: productsAvg
    };
  }

  /**
   * Generates chart's tooltip and note text
   * @param seriesData the seriesData item
   * @param nUptakeLabel optional n-Uptake label
   * @private
   */
  private createTooltipText(seriesData: GenericSeriesData, nUptakeLabel?: string): string {
    const nUptake = this.roundNumericService.roundAsNumber(seriesData.xValue, 0);
    const water = this.roundNumericService.roundAsNumber(seriesData.yValue, 0);
    return `${this.translationService.translate('Global__Water')}: ${water} \n ${this.translationService.translate(nUptakeLabel ?? 'N__ChartTitleX')}: ${nUptake}`;
  }
}
